import { useContext, useState } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SnackbarContext } from "@/context/SnackbarProvider";
import MetricValueCard from "../../../Common/MetricValueCard";
import IconLoader from "@/features/Common/components/IconLoader";
import { getWellnessFlagIconProps } from "../../../Common/wellnessFlagIconProps";
import { WellnessScoreStatus } from "../../../Common/types";
import { submitHealthProfileEntry } from "@/features/HealthScore/utils/submitHealthProfileEntry";
import { runSubmitWithSnackbar } from "@/features/HealthScore/hooks/useSubmitVitalForm";
import { getBmiStatus, getBmiDisplayValue } from "./utils";
import BMIUpdateForm from "../BMIUpdateForm";

export interface BMIInfoCardProps {
  bmi: number;
  weightKg: number;
  heightCm: number;
  lastUpdated?: string;
  onCtaClick?: () => void;
  /** Called when weight/height are saved; use to trigger get-health-profile refetch */
  onMetricUpdated?: () => void;
}

export default function BMIInfoCard({
  bmi,
  weightKg,
  heightCm,
  lastUpdated,
  onCtaClick,
  onMetricUpdated,
}: BMIInfoCardProps) {
  const { t } = useTranslation();
  const { showSnackbar } = useContext(SnackbarContext);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const status = getBmiStatus(bmi);
  const metricValue = getBmiDisplayValue(bmi);

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
      return;
    }
    setIsUpdateModalOpen(true);
  };

  const handleSave = (
    weightKgSaved: number,
    heightCmSaved: number,
    heightUnit: "cm" | "m"
  ) => {
    const heightValue =
      heightUnit === "cm"
        ? String(heightCmSaved)
        : (heightCmSaved / 100).toFixed(2);
    const heightApiUnit = heightUnit === "cm" ? "CM" : "M";
    const promise = (async () => {
      await submitHealthProfileEntry("Weight", String(weightKgSaved));
      await submitHealthProfileEntry("Height", heightValue, {
        unit: heightApiUnit,
      });
    })();
    runSubmitWithSnackbar(showSnackbar, promise, {
      errorMessage: t(
        "Metric.manualEntry.updateError",
        "Unable to save. Please try again."
      ),
      onSuccess: () => {
        onMetricUpdated?.();
        setIsUpdateModalOpen(false);
      },
    });
  };

  const renderStatusMessage = () => {
    const messageKey =
      status === WellnessScoreStatus.NeedsAttention
        ? "HealthHistory.bmi.message.needsAttention"
        : status === WellnessScoreStatus.NeedsSupport
          ? "HealthHistory.bmi.message.needsSupport"
          : null;

    if (!messageKey) {
      return null;
    }

    const isNeedsSupport = status === WellnessScoreStatus.NeedsSupport;

    return (
      <Stack
        sx={{
          backgroundColor: isNeedsSupport ? "error.light" : "secondary.100",
          borderRadius: 2,
          p: 2,
        }}
        flexDirection="row"
        gap={1.5}
      >
        <IconLoader icon="FlagIcon" {...getWellnessFlagIconProps(status)} />
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            color: "neutral.700",
          }}
        >
          {t(messageKey)}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <MetricValueCard
        metricName={t("HealthHistory.bmi.title")}
        metricValue={metricValue}
        status={status}
        lastUpdated={lastUpdated}
        ctaText={t("HealthHistory.bmi.cta")}
        onCtaClick={handleCtaClick}
      >
        <Stack gap={1.5}>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{ fontSize: 16, fontWeight: 500, color: "neutral.700" }}
            >
              {t("HealthHistory.bmi.weight")}
            </Typography>
            <Typography
              sx={{ fontSize: 16, fontWeight: 500, color: "neutral.900" }}
            >
              {weightKg}kg
            </Typography>
          </Stack>
          <Divider />
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{ fontSize: 16, fontWeight: 500, color: "neutral.700" }}
            >
              {t("HealthHistory.bmi.height")}
            </Typography>
            <Typography
              sx={{ fontSize: 16, fontWeight: 500, color: "neutral.900" }}
            >
              {heightCm}cm
            </Typography>
          </Stack>
          {renderStatusMessage()}
        </Stack>
      </MetricValueCard>

      {isUpdateModalOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            bgcolor: "background.paper",
            overflow: "auto",
          }}
        >
          <BMIUpdateForm
            onBack={() => setIsUpdateModalOpen(false)}
            onSave={handleSave}
          />
        </Box>
      )}
    </>
  );
}
